import { Link, useLocation } from 'react-router-dom';
import {
  ArrowRight,
  CalendarClock,
  CheckCircle2,
  CreditCard,
  FileSearch,
  FileText,
  MapPinned,
  Route,
  ShieldCheck,
} from 'lucide-react';
import { Logo } from '../components/waitlist/Logo';
import { PlayStoreBadge } from '../components/waitlist/PlayStoreBadge';
import { getPlayStoreUrl } from '../config/urls';
import { usePageMeta } from '../hooks/usePageMeta';

import heroParkingContext from '../../assets/premium-hero-parking-context.webp';
import mapScreen from '../../assets/Homscreen.png';
import addTicketScreen from '../../assets/app-screenshot-add-ticket-portrait.png';
import coachAnalysisScreen from '../../assets/app-screenshot-coach-analysis-portrait.png';
import ticketDetailsScreen from '../../assets/app-screenshot-ticket-details-portrait.png';
import appealScreen from '../../assets/app-screenshot-appeal-letter-portrait.png';
import testimonialDriverCafe from '../../assets/testimonial-driver-cafe.webp';
import testimonialDriverCar from '../../assets/testimonial-driver-car.webp';
import testimonialDriverStreet from '../../assets/testimonial-driver-street.webp';

const trustSignals = ['Free to download', 'Premium AI tools', 'Driver stays in control'] as const;

const workflow = [
  {
    label: 'SCAN TO CASE',
    title: 'Scan or upload the notice',
    body: 'Use the camera or upload a ticket image, then review OCR fields such as PCN number, authority, amount, dates, and vehicle registration.',
    icon: FileSearch,
  },
  {
    label: 'DEADLINE CONTROL',
    title: 'Keep the case timeline visible',
    body: 'Discount windows, appeal deadlines, payment dates, TEC dates, and status updates stay attached to the ticket record.',
    icon: CalendarClock,
  },
  {
    label: 'PLUS FEATURES',
    title: 'Use AI when the case needs more',
    body: 'Subscription unlocks deeper PCN analysis, evidence-based appeal drafts, AI routing, PDFs, and managed submission support.',
    icon: ShieldCheck,
  },
] as const;

const productEvidence = [
  {
    label: 'ZONE AWARENESS',
    title: 'Understand parking risk before the next stop',
    body: 'The map gives drivers context around parking zones so they can avoid repeat PCNs in familiar problem areas.',
    src: mapScreen,
    alt: 'PCNGuard map screen showing parking zones',
    icon: MapPinned,
  },
  {
    label: 'TICKET HUB',
    title: 'Turn a notice into a structured case',
    body: 'A scanned or uploaded ticket becomes a case record with extracted details, search, filters, archive states, and ticket history.',
    src: addTicketScreen,
    alt: 'PCNGuard add ticket screen',
    icon: FileSearch,
  },
  {
    label: 'COACH ANALYSIS',
    title: 'Know what evidence the case needs',
    body: 'PCNGuard Plus can analyse the ticket, surface weak points, and guide the evidence kit before a driver writes a response.',
    src: coachAnalysisScreen,
    alt: 'PCNGuard coach analysis screen',
    icon: ShieldCheck,
  },
  {
    label: 'APPEAL DRAFT',
    title: 'Review a draft before taking action',
    body: 'Appeal drafts give drivers a structured starting point while keeping review, edits, and the final decision in their hands.',
    src: appealScreen,
    alt: 'PCNGuard appeal letter draft screen',
    icon: FileText,
  },
] as const;

const accessModel = [
  {
    label: 'Free download',
    title: 'Start with the driver app',
    body: 'Download PCNGuard on Android, create an account, add vehicle details, and keep parking tickets organized in one place.',
    icon: FileSearch,
  },
  {
    label: 'PCNGuard Plus',
    title: 'Unlock the AI-heavy work',
    body: 'Subscription unlocks unlimited AI scans/uploads, PCN analysis, evidence-based appeal drafts, PDF export, AI routing, and monthly managed credits.',
    icon: CreditCard,
  },
  {
    label: 'Annual option',
    title: 'More credits for active drivers',
    body: 'The annual plan adds bonus managed credits and early-access benefits for drivers who handle tickets more often.',
    icon: Route,
  },
] as const;

const heroOutcomes = [
  {
    label: 'Discount protected',
    value: '26 Jan',
    detail: 'Keep the cheaper payment window visible.',
  },
  {
    label: 'Appeal date tracked',
    value: '10 Feb',
    detail: 'Know the response deadline before it becomes urgent.',
  },
  {
    label: 'Draft reviewed',
    value: 'Ready',
    detail: 'Start from structured wording, then edit before use.',
  },
] as const;

const driverFaqs = [
  {
    q: 'What does PCNGuard do for drivers?',
    a: 'PCNGuard helps UK drivers scan or upload PCNs, review extracted ticket details, track deadlines, organize evidence, use AI coach analysis, prepare appeal drafts, and understand parking-zone risk.',
  },
  {
    q: 'Is the app free to download?',
    a: 'Yes. The Android app is free to download. Some tools, including unlimited AI scans/uploads, AI PCN analysis, evidence-based appeal drafts, AI routing, PDF export, and managed submission support require PCNGuard Plus.',
  },
  {
    q: 'Does PCNGuard submit appeals automatically?',
    a: 'No. PCNGuard prepares structured draft content and keeps the case organized, but the driver reviews, edits, and decides how to use the response.',
  },
  {
    q: 'What ticket information can it extract?',
    a: 'The app is designed to extract fields such as PCN number, issuing authority, vehicle registration, location, dates, fine amount, discount deadline, and appeal deadline where they are visible in the uploaded notice.',
  },
  {
    q: 'Is FleetMode different from the driver app?',
    a: 'Yes. The driver app is for individual cases. FleetMode is for PCO hire companies handling PCN volume, driver-liability readiness, transfer submission preparation, deadline risk, evidence gaps, and recovery status.',
  },
] as const;

const driverTestimonials = [
  {
    badge: 'Money saved',
    quote: 'PCNGuard showed the discount deadline, organized the notice, and helped me avoid missing the cheaper payment window while I prepared my response.',
    name: 'Daniel Mitchell',
    detail: 'Uses PCNGuard for ticket tracking and reminders',
    image: testimonialDriverCar,
    alt: 'Driver checking PCNGuard from a parked car',
  },
  {
    badge: 'Appeal ready',
    quote: 'The app drafted a ready-to-send appeal from my ticket details. I reviewed it, adjusted the wording, and had something structured instead of starting from zero.',
    name: 'Aisha Khan',
    detail: 'Uses PCNGuard Plus for appeal draft review',
    image: testimonialDriverStreet,
    alt: 'Driver reviewing a parking notice beside a parked car',
  },
  {
    badge: 'AI coach',
    quote: 'The AI coach explained the best appeal strategy and the evidence I needed, so I knew what photos and documents to gather before sending anything.',
    name: 'Ravi Patel',
    detail: 'Uses PCNGuard to organize repeat parking cases',
    image: testimonialDriverCafe,
    alt: 'Driver sitting near a window after organizing a PCN case',
  },
] as const;

export function PremiumConcept() {
  const location = useLocation();
  const isPreviewRoute = location.pathname === '/premium-concept';

  usePageMeta({
    title: isPreviewRoute ? 'PCNGuard Premium Website Concept' : 'PCNGuard - UK Parking Ticket Appeal App for Drivers',
    description: isPreviewRoute
      ? 'PCNGuard premium website concept preview for the UK driver app marketing experience.'
      : 'Free Android app for UK drivers to scan PCNs, track discount deadlines, use AI coach analysis, draft ready-to-send appeals, and avoid repeat parking tickets.',
    canonical: isPreviewRoute ? 'https://pcnguard.co.uk/premium-concept' : 'https://pcnguard.co.uk/',
    ogTitle: isPreviewRoute ? 'PCNGuard Premium Website Concept' : 'PCNGuard - UK Parking Ticket Appeal App',
    ogDescription: isPreviewRoute
      ? 'Premium website concept preview for PCNGuard.'
      : 'Scan a PCN, understand deadlines, use AI appeal guidance, draft a response, and keep parking ticket cases under control.',
    robots: isPreviewRoute ? 'noindex,nofollow' : 'index,follow',
  });

  const playStoreUrl = getPlayStoreUrl();

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#fff7eb] text-[#081b1f] font-sans selection:bg-[#d79b45]/30">
      <nav className="fixed inset-x-0 top-0 z-50 border-b border-[#eadcc8]/80 bg-[#fff7eb]/90 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:h-20 sm:px-6 lg:px-8">
          <Link to="/" className="flex min-w-0 items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-[#e4d4bf] bg-white p-1 shadow-sm sm:h-11 sm:w-11">
              <Logo className="h-full w-full scale-150 object-contain" />
            </span>
            <span className="truncate font-space text-xl font-bold tracking-tight text-[#081b1f] sm:text-2xl">PCNGuard</span>
          </Link>

          <div className="hidden items-center gap-6 text-sm font-semibold text-[#425457] lg:flex">
            <a href="#how-it-works" className="transition-colors hover:text-[#174a39]">How it works</a>
            <a href="#premium" className="transition-colors hover:text-[#174a39]">Premium</a>
            <a href="#outcomes" className="transition-colors hover:text-[#174a39]">Outcomes</a>
            <Link to="/fleet" className="transition-colors hover:text-[#174a39]">PCO Hire</Link>
            <a href="#faq" className="transition-colors hover:text-[#174a39]">FAQ</a>
            <a
              href={playStoreUrl || undefined}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center rounded-md bg-[#174a39] px-4 py-2 text-sm font-bold text-white shadow-sm shadow-[#174a39]/20 transition hover:bg-[#25624b] active:translate-y-px"
            >
              Download
            </a>
          </div>

          <div className="hidden shrink-0 items-center gap-2 sm:flex lg:hidden">
            <a
              href={playStoreUrl || undefined}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center rounded-md bg-[#174a39] px-4 py-2 text-sm font-bold text-white shadow-sm shadow-[#174a39]/20 transition hover:bg-[#25624b] active:translate-y-px"
            >
              Download
            </a>
          </div>
        </div>
      </nav>

      <main>
        <section className="relative min-h-[760px] px-4 pb-14 pt-24 sm:px-6 sm:pb-20 sm:pt-28 lg:min-h-[860px]">
          <img
            src={heroParkingContext}
            alt="Parked vehicle with parking notice in a premium UK street context"
            className="absolute inset-0 h-full w-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-[linear-gradient(90deg,#fff7eb_0%,rgba(255,247,235,0.97)_35%,rgba(255,247,235,0.52)_66%,rgba(8,27,31,0.10)_100%)]" />
          <div className="absolute inset-0 bg-[linear-gradient(0deg,#fff7eb_0%,rgba(255,247,235,0.15)_34%,rgba(255,247,235,0.50)_100%)]" />

          <div className="relative mx-auto grid max-w-7xl items-center gap-10 lg:grid-cols-[0.92fr_1.08fr]">
            <div className="max-w-2xl animate-[conceptRise_0.7s_ease-out_both]">
              <div className="inline-flex items-center gap-2 rounded-md border border-[#d7c3a5] bg-[#fffaf2]/75 px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.18em] text-[#86591d] shadow-sm backdrop-blur">
                Android driver app
              </div>

              <h1 className="mt-6 max-w-[22rem] font-space text-[2.55rem] font-bold leading-[0.98] tracking-tight text-[#081b1f] sm:max-w-2xl sm:text-6xl lg:text-[5.35rem]">
                Turn a parking ticket into a controlled case
              </h1>

              <p className="mt-6 max-w-[22rem] text-base leading-8 text-[#3d4f51] sm:max-w-xl sm:text-lg">
                Download the free Android app to scan or upload a PCN, keep the important dates visible, and unlock AI-powered analysis, routing, evidence, and appeal tools when you need them.
              </p>

              <div className="mt-8 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
                <a
                  href={playStoreUrl || undefined}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-[#174a39] px-5 py-3 text-sm font-bold text-white shadow-sm shadow-[#174a39]/20 transition hover:bg-[#25624b] active:translate-y-px"
                >
                  Download the app
                  <ArrowRight className="h-4 w-4" />
                </a>
              </div>

              <div className="mt-8 flex max-w-xl flex-wrap gap-x-7 gap-y-3 text-sm font-medium text-[#425457]">
                {trustSignals.map((item) => (
                  <div key={item} className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-[#174a39]" />
                    <span className="whitespace-nowrap">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative hidden min-h-[650px] lg:block">
              <div className="absolute right-0 top-10 grid w-[620px] grid-cols-[0.82fr_1fr] overflow-hidden border border-[#e2d1b8] bg-[#fffaf2]/94 shadow-[0_32px_90px_rgba(8,27,31,0.18)] backdrop-blur">
                <div className="flex flex-col justify-between border-r border-[#e2d1b8] p-5">
                  <div>
                    <div className="font-mono text-xs uppercase tracking-[0.18em] text-[#86591d]">Driver outcomes</div>
                    <h2 className="mt-3 font-space text-3xl font-bold leading-tight">Know the next move</h2>
                    <p className="mt-3 text-sm leading-6 text-[#5d6c69]">
                      PCNGuard turns a notice into practical next steps: extracted fields, deadline visibility, evidence guidance, and draft review.
                    </p>
                  </div>

                  <div className="mt-6 grid gap-3">
                    {heroOutcomes.map((item) => (
                      <div key={item.label} className="border-l-4 border-[#174a39] bg-white px-4 py-3 shadow-sm">
                        <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#86591d]">{item.label}</div>
                        <div className="mt-1 font-space text-2xl font-bold">{item.value}</div>
                        <p className="mt-1 text-xs leading-5 text-[#5d6c69]">{item.detail}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="relative flex min-h-[560px] items-end justify-center overflow-hidden bg-[#efe4d4] px-8 pt-8">
                  <img
                    src={ticketDetailsScreen}
                    alt="PCNGuard ticket details screen showing discount and appeal deadlines"
                    className="h-[610px] w-auto rounded-[32px] object-contain object-top drop-shadow-[0_24px_46px_rgba(8,27,31,0.24)]"
                    loading="eager"
                    decoding="sync"
                  />
                  <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-[#efe4d4] to-transparent" />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="how-it-works" className="scroll-mt-24 border-y border-[#e7d8c3] bg-[#f3e8d7] px-4 py-8 sm:px-6">
          <div className="mx-auto grid max-w-7xl gap-4 md:grid-cols-3">
            {workflow.map((item) => (
              <article key={item.label} className="grid min-w-0 grid-cols-1 gap-4 border-l-4 border-[#174a39] bg-[#fffaf2] p-5 shadow-sm sm:grid-cols-[2.75rem_1fr] md:min-h-[170px]">
                <div className="flex h-11 w-11 items-center justify-center rounded-md bg-[#e4f0e9] text-[#174a39]">
                  <item.icon className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#86591d]">{item.label}</div>
                  <h2 className="mt-1 font-space text-xl font-bold text-[#081b1f]">{item.title}</h2>
                  <p className="mt-2 max-w-[18rem] text-sm leading-6 text-[#5d6c69] sm:max-w-none">{item.body}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section id="premium" className="scroll-mt-24 border-y border-[#e7d8c3] bg-[#f3e8d7] px-4 py-16 sm:px-6 sm:py-20">
          <div className="mx-auto max-w-7xl">
            <div className="grid gap-8 lg:grid-cols-[0.82fr_1.18fr] lg:items-end">
              <div>
                <div className="inline-flex rounded-md border border-[#dcc8aa] bg-[#fffaf2] px-3 py-1.5 font-mono text-xs uppercase tracking-[0.18em] text-[#86591d]">
                  Free app, paid power tools
                </div>
                <h2 className="mt-5 font-space text-4xl font-bold leading-tight tracking-tight text-[#081b1f] sm:text-5xl">
                  Download free. Subscribe when the case needs AI depth.
                </h2>
              </div>
              <p className="max-w-2xl text-lg leading-8 text-[#3d4f51]">
                Drivers can install PCNGuard for free. PCNGuard Plus unlocks the heavier AI workflow: unlimited scans/uploads, coach analysis, appeal drafts, AI routing, PDFs, and managed-service credits.
              </p>
            </div>

            <div className="mt-10 grid gap-5 lg:grid-cols-3">
              {accessModel.map((item) => (
                <article key={item.label} className="border border-[#e3d4bf] bg-[#fffaf2] p-6 shadow-sm">
                  <div className="flex h-11 w-11 items-center justify-center rounded-md bg-[#e4f0e9] text-[#174a39]">
                    <item.icon className="h-5 w-5" />
                  </div>
                  <div className="mt-5 font-mono text-[11px] uppercase tracking-[0.18em] text-[#86591d]">{item.label}</div>
                  <h3 className="mt-2 font-space text-2xl font-bold text-[#081b1f]">{item.title}</h3>
                  <p className="mt-3 text-sm leading-6 text-[#5d6c69]">{item.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="outcomes" className="scroll-mt-24 bg-[#fff7eb] px-4 py-16 sm:px-6 sm:py-24">
          <div className="mx-auto max-w-7xl">
            <div className="grid min-w-0 gap-8 lg:grid-cols-[0.78fr_1.22fr] lg:items-end">
              <div>
                <div className="inline-flex rounded-md border border-[#dcc8aa] px-3 py-1.5 font-mono text-xs uppercase tracking-[0.18em] text-[#86591d]">
                  Driver outcomes
                </div>
                <h2 className="mt-5 font-space text-4xl font-bold leading-tight tracking-tight text-[#081b1f] sm:text-5xl">
                  What changes when a driver uses PCNGuard.
                </h2>
              </div>
              <p className="min-w-0 max-w-2xl text-lg leading-8 text-[#3d4f51]">
                PCNGuard helps drivers move from uncertainty to a clear next step: understand the notice, protect key dates, gather evidence, and review a response before acting.
              </p>
            </div>

            <div className="mt-10 grid items-stretch gap-5 md:grid-cols-2 xl:grid-cols-4">
              {productEvidence.map((item) => (
                <article
                  key={item.label}
                  className="group flex h-full flex-col overflow-hidden border border-[#e3d4bf] bg-[#f8efe2] shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-[0_18px_55px_rgba(8,27,31,0.12)]"
                >
                  <div className="relative flex h-[340px] items-start justify-center overflow-hidden bg-[#efe4d4] px-6 pt-6 sm:h-[390px] sm:px-8 sm:pt-8 xl:h-[340px]">
                    <img
                      src={item.src}
                      alt={item.alt}
                      className="h-[390px] w-auto max-w-full rounded-[24px] object-contain object-top drop-shadow-[0_22px_38px_rgba(8,27,31,0.22)] transition duration-500 group-hover:scale-[1.025] sm:h-[480px] sm:rounded-[28px] xl:h-[390px]"
                      loading="eager"
                      decoding="sync"
                    />
                    <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-[#f8efe2] to-transparent" />
                  </div>
                  <div className="flex flex-1 flex-col p-5">
                    <div className="flex items-center gap-3">
                      <span className="flex h-10 w-10 items-center justify-center rounded-md bg-[#e4f0e9] text-[#174a39]">
                        <item.icon className="h-5 w-5" />
                      </span>
                      <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#86591d]">{item.label}</div>
                    </div>
                    <h3 className="mt-4 font-space text-2xl font-bold text-[#081b1f]">{item.title}</h3>
                    <p className="mt-2 text-sm leading-6 text-[#5d6c69]">{item.body}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="driver-testimonials" className="scroll-mt-24 bg-[#fff7eb] px-4 py-16 sm:px-6 sm:py-24" aria-label="Trusted driver testimonials">
          <div className="mx-auto max-w-7xl">
            <div className="grid gap-8 lg:grid-cols-[0.78fr_1.22fr] lg:items-end">
              <div>
                <div className="inline-flex rounded-md border border-[#dcc8aa] px-3 py-1.5 font-mono text-xs uppercase tracking-[0.18em] text-[#86591d]">
                  Driver stories
                </div>
                <h2 className="mt-5 font-space text-4xl font-bold leading-tight tracking-tight text-[#081b1f] sm:text-5xl">
                  Trusted Driver Testimonials
                </h2>
              </div>
              <p className="max-w-2xl text-lg leading-8 text-[#3d4f51]">
                Drivers use PCNGuard to protect discount windows, draft ready-to-send appeals, understand appeal strategy, and gather the evidence needed before they respond.
              </p>
            </div>

            <div className="mt-10 grid gap-5 lg:grid-cols-3">
              {driverTestimonials.map((item) => (
                <article key={item.name} className="group overflow-hidden border border-[#e3d4bf] bg-[#f8efe2] shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-[0_18px_55px_rgba(8,27,31,0.12)]">
                  <div className="relative h-64 overflow-hidden bg-[#efe4d4]">
                    <div
                      role="img"
                      aria-label={item.alt}
                      className="h-full w-full bg-cover bg-center transition duration-500 group-hover:scale-[1.025]"
                      style={{ backgroundImage: `url(${item.image})` }}
                    />
                    <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#f8efe2] to-transparent" />
                  </div>
                  <div className="p-5 sm:p-6">
                    <div className="inline-flex rounded-md border border-[#dcc8aa] bg-[#fffaf2] px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.18em] text-[#86591d]">
                      {item.badge}
                    </div>
                    <blockquote className="mt-5">
                      <p className="font-space text-xl font-bold leading-8 text-[#081b1f]">&ldquo;{item.quote}&rdquo;</p>
                    </blockquote>
                    <div className="mt-6 border-t border-[#e3d4bf] pt-4">
                      <cite className="not-italic font-space text-base font-bold text-[#081b1f]">{item.name}</cite>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="faq" className="scroll-mt-24 border-y border-[#e7d8c3] bg-[#f3e8d7] px-4 py-16 sm:px-6 sm:py-20" aria-label="Frequently asked questions">
          <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[0.78fr_1.22fr]">
            <div>
              <div className="inline-flex rounded-md border border-[#dcc8aa] bg-[#fffaf2] px-3 py-1.5 font-mono text-xs uppercase tracking-[0.18em] text-[#86591d]">
                FAQ
              </div>
              <h2 className="mt-5 font-space text-4xl font-bold tracking-tight text-[#081b1f]">Driver app questions</h2>
              <p className="mt-4 max-w-md text-base leading-7 text-[#5d6c69]">
                Answers about AI drafts, extracted ticket data, review control, and the difference between the driver app and FleetMode.
              </p>
            </div>
            <div className="grid gap-3">
              {driverFaqs.map((faq) => (
                <details key={faq.q} className="group border border-[#e3d4bf] bg-[#fffaf2] shadow-sm">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 p-5 font-space text-lg font-bold text-[#081b1f] [&::-webkit-details-marker]:hidden">
                    {faq.q}
                    <span className="text-[#86591d] transition group-open:rotate-45">+</span>
                  </summary>
                  <div className="px-5 pb-5 text-sm leading-7 text-[#5d6c69]">{faq.a}</div>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="border-y border-[#dcc8aa] bg-[#174a39] px-4 py-14 text-white sm:px-6">
          <div className="mx-auto flex max-w-7xl flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div>
              <div className="font-mono text-xs uppercase tracking-[0.18em] text-[#e5a955]">Driver app</div>
              <h2 className="mt-3 font-space text-3xl font-bold tracking-tight">Keep your next parking ticket under control.</h2>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-[#d6e5df]">
                Run a PCO hire company? FleetMode has a separate workspace for team PCN control.
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <PlayStoreBadge href={playStoreUrl || undefined} variant="light" className="h-12" />
              <Link
                to="/fleet"
                className="inline-flex items-center justify-center gap-2 rounded-md border border-white/25 bg-white/10 px-5 py-3 text-sm font-bold text-white transition hover:bg-white/16 active:translate-y-px"
              >
                See FleetMode
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      </main>

      <footer className="bg-[#fff7eb] px-4 py-10 text-[#081b1f] sm:px-6">
        <div className="mx-auto grid max-w-7xl gap-8 md:grid-cols-[1fr_auto] md:items-end">
          <div>
            <Link to="/" className="inline-flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg border border-[#dcc8aa] bg-white p-1">
                <Logo className="h-full w-full scale-150 object-contain" />
              </span>
              <span className="font-space text-xl font-bold tracking-tight">PCNGuard</span>
            </Link>
            <p className="mt-4 max-w-md text-sm leading-6 text-[#4d605d]">
              Parking ticket control for UK drivers.
            </p>
          </div>

          <div className="grid gap-4 text-sm md:text-right">
            <div className="flex flex-wrap gap-x-5 gap-y-2 md:justify-end">
              <Link to="/" className="text-[#334846] transition hover:text-[#174a39]">Driver App</Link>
              <Link to="/fleet" className="text-[#334846] transition hover:text-[#174a39]">FleetMode</Link>
              <Link to="/privacy" className="text-[#334846] transition hover:text-[#174a39]">Privacy</Link>
              <Link to="/terms" className="text-[#334846] transition hover:text-[#174a39]">Terms</Link>
            </div>
            <a href="mailto:info@pcnguard.co.uk" className="font-mono text-xs uppercase tracking-[0.18em] text-[#86591d]">
              info@pcnguard.co.uk
            </a>
            <div className="text-xs text-[#6f817e]">PCNGuard &copy; 2026</div>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes conceptRise {
          0% { opacity: 0; transform: translateY(18px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

export default PremiumConcept;
